<script>
	import { Locale, Portal } from "@xbs/svelte-wx";
	import { en as cEN } from "@xbs/wx-core-locales";
	import en from "../locales/en";
	import { EventBusRouter } from "@xbs/lib-state";
	import {
		DataStore,
		selection,
		taskMenu,
		userMenu,
		uid,
		keyNavigation,
		defaultTaskShape,
		addTask,
		tagFilter,
		listEditor,
		getApi,
		isDefined,
		dragManager,
		defaultDragConfig,
		defaultPriorities,
	} from "@xbs/lib-todo";

	import Material from "../themes/Material.svelte";
	import Tree from "./Tree/Tree.svelte";
	import AddButton from "./AddButton.svelte";

	import { writable } from "svelte/store";
	import { createEventDispatcher } from "svelte";

	export let id = uid();
	export let tasks = [];
	export let projects = [];
	export let users = [];
	export let tags = [];
	export let selected = [];
	export let activeProject = undefined;
	export let readonly = false;
	export let taskShape = defaultTaskShape;
	export let drag = defaultDragConfig;
	export let priorities = null;
	export let menu = true;
	export let history = true;

	const dispatch = createEventDispatcher();
	const dataStore = new DataStore(writable, { history });

	$: dataStore.init({
		tasks,
		users,
		tags,
		selected,
		taskShape: {
			...defaultTaskShape,
			...taskShape,
		},
		drag: drag ? { ...defaultDragConfig, ...drag } : drag,
		priorities:
			(Array.isArray(priorities) && priorities) || defaultPriorities,
		projects,
		activeProject,
		readonly,
		id,
		menu,
	});

	// define event route
	let lastInRoute = new EventBusRouter(dispatch);
	// public API
	export const api = getApi(dataStore, lastInRoute);

	dataStore.out.setNext(lastInRoute.exec);

	function triggerAction(event) {
		const { action, data } = event.detail;
		dataStore.in.exec(action, data);
	}

	function handleAddTask(event) {
		if (isDefined(dataStore.getState().filter)) return;

		event.stopPropagation();
		addTask({
			store: dataStore,
			reverse: true,
		});
	}

</script>

<svelte:window
	use:keyNavigation={{ api, widgetId: id }}
	use:listEditor={{ api, readonly }} />

<Locale words={{ ...en, ...cEN }} optional={true}>
	<Material>
		<div class="wx-todo" data-todo-id={id}>
			{#if !readonly}
				<AddButton on:click={handleAddTask} />
			{/if}
			<div
				class="wx-todo__wrapper"
				on:contextmenu={event => event.preventDefault()}
				use:tagFilter={{ api }}
				use:selection={{ api, readonly }}
				use:taskMenu={{ api, readonly }}
				use:dragManager={{ api, readonly: !drag || readonly, id }}
				use:userMenu={{ api }}>
				<Tree on:action={triggerAction} {api} store={dataStore} />
			</div>
		</div>
	</Material>
</Locale>

<style>
	.wx-todo {
		display: flex;
		flex-direction: column;

		background: var(--wx-background);
		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		line-height: var(--wx-line-height);
		color: var(--wx-color-font);

		margin: 0;
		padding: 0;
		height: 100%;
		width: 100%;
		max-width: 100%;
		max-height: 100%;
		z-index: 1;

		overflow: hidden;
	}
	.wx-todo :global(.wx-todo__dragged-task) {
		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		line-height: var(--wx-line-height);
		color: var(--wx-color-font);
		background: var(--wx-background);

		position: fixed;
		z-index: 999999;

		touch-action: none;
		user-select: none;
		pointer-events: none;

		box-shadow: 0 4px 8px rgba(116, 116, 116, 0.3);
	}
	.wx-todo :global(.wx-todo__dragged-task:after) {
		content: var(--wx-todo-dragged-tasks-count);

		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;

		background: var(--wx-color-success);
		color: var(--wx-color-secondary-font);

		width: 30px;
		height: 30px;
		border-radius: 50%;
		top: -10px;
		right: -10px;
		z-index: 4;
	}
	.wx-todo :global(.wx-todo_dropped-line) {
		position: fixed;

		border-radius: 2px;
		background-color: var(--wx-color-secondary-light);

		touch-action: none;
		user-select: none;
		pointer-events: none;

		z-index: 2;
	}
	.wx-todo__wrapper {
		display: flex;
		overflow-y: auto;
		height: 100%;
		width: 100%;
	}
	:global(.wx-todo_filter) {
		background: var(--wx-todo-mark-background);
	}
	:global(.wx-todo_filter--strict) {
		background: var(--wx-todo-mark-background-strict);
	}
	:global(.wx-todo_tag) {
		cursor: pointer;
		color: var(--wx-color-primary);
	}
	:global(.wx-todo_date--current) {
		color: var(--wx-todo-date-color-current);
	}
	:global(.wx-todo_date--expired) {
		color: var(--wx-todo-date-color-expired);
	}
	:global(.wx-ondrag--touch) {
		overflow: hidden;
	}
	:global(.wx-todo) {
		user-select: none;
	}

</style>
